// Authentication via Firebase / Identity Platform (migration phase 3 — the
// Supabase Auth predecessor of this file lives in git history). The exported
// surface is kept identical so authStore and every view stay backend-agnostic.
//
// Native (Capacitor) provider sign-ins go through the
// @capacitor-firebase/authentication plugin (native Google/Apple sheets), and
// the resulting credential is replayed into the web SDK so the JS session —
// whose ID token apiClient sends to the Go backend — is always authoritative.

import { Capacitor } from "@capacitor/core"
import { FirebaseAuthentication } from "@capacitor-firebase/authentication"
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  type User as FirebaseUser,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  signInAnonymously as firebaseSignInAnonymously,
  updatePassword as firebaseUpdatePassword,
  updateProfile as firebaseUpdateProfile,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth"
import { logger } from "../utils/logger"
import { auth } from "./firebase"

export interface AuthUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
  isAnonymous: boolean
  emailVerified: boolean
  phoneNumber: string | null
  providerId: string
  metadata: {
    creationTime: string | null
    lastSignInTime: string | null
  }
}

export interface RegisterData {
  email: string
  password: string
  displayName?: string
}

export interface LoginData {
  email: string
  password: string
}

export interface RegisterResult {
  user: AuthUser
  // Firebase signs the user in immediately on registration (no
  // confirm-before-session mode like Supabase had), so this is always false —
  // kept so the registration views don't need to change.
  confirmationRequired: boolean
}

const googleProvider = new GoogleAuthProvider()
googleProvider.setCustomParameters({ prompt: "select_account" })
const appleProvider = new OAuthProvider("apple.com")

function formatUser(user: FirebaseUser): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    isAnonymous: user.isAnonymous,
    emailVerified: user.emailVerified,
    phoneNumber: user.phoneNumber,
    providerId: user.providerData[0]?.providerId ?? user.providerId,
    metadata: {
      creationTime: user.metadata.creationTime || null,
      lastSignInTime: user.metadata.lastSignInTime || null,
    },
  }
}

// Human-readable messages for the auth error codes users can actually hit.
// Anything unmapped falls back to the code itself (still actionable in a bug
// report, never a raw JSON blob).
function authErrorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code ?? ""
  const known: Record<string, string> = {
    "auth/email-already-in-use": "An account with this email already exists",
    "auth/invalid-email": "Invalid email address",
    "auth/weak-password": "Password is too weak (minimum 6 characters)",
    "auth/user-not-found": "Invalid email or password",
    "auth/wrong-password": "Invalid email or password",
    "auth/invalid-credential": "Invalid email or password",
    "auth/too-many-requests": "Too many attempts — try again later",
    "auth/popup-closed-by-user": "Sign-in was cancelled",
    "auth/network-request-failed": "Network error — check your connection",
    "auth/requires-recent-login": "Please sign in again to make this change",
    "auth/credential-already-in-use": "This account is already linked to another user",
  }
  if (known[code]) return known[code]
  const message = (error as { message?: string })?.message
  return code || message || "Authentication failed"
}

class AuthService {
  // Latest user, kept fresh so getCurrentUser() can stay synchronous.
  private cachedUser: AuthUser | null = null

  // Password recovery: when the reset email's action link is configured to
  // come back to the app, it arrives as ?mode=resetPassword&oobCode=... —
  // latch it so the set-new-password UI can pick it up whenever it mounts
  // (same contract the Supabase PASSWORD_RECOVERY event had).
  private recoveryOobCode: string | null = null
  private passwordRecoveryListeners = new Set<() => void>()

  constructor() {
    firebaseOnAuthStateChanged(auth, (user) => {
      this.cachedUser = user ? formatUser(user) : null
    })

    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)
      if (params.get("mode") === "resetPassword" && params.get("oobCode")) {
        this.recoveryOobCode = params.get("oobCode")
        for (const listener of this.passwordRecoveryListeners) listener()
      }
    }
  }

  async register({ email, password, displayName }: RegisterData): Promise<RegisterResult> {
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password)
      if (displayName) {
        await firebaseUpdateProfile(credential.user, { displayName })
      }
      return { user: formatUser(credential.user), confirmationRequired: false }
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  async login({ email, password }: LoginData): Promise<AuthUser> {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password)
      return formatUser(credential.user)
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  // Native: the plugin shows the OS account sheet and returns a provider ID
  // token, which we replay into the web SDK (signInWithCredential) so the JS
  // session exists for apiClient. Web: standard popup.
  async signInWithGoogle(): Promise<AuthUser> {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle()
        const idToken = result.credential?.idToken
        if (!idToken) throw new Error("No ID token received from Google Sign-In")
        const credential = GoogleAuthProvider.credential(idToken)
        return formatUser((await signInWithCredential(auth, credential)).user)
      }
      return formatUser((await signInWithPopup(auth, googleProvider)).user)
    } catch (error) {
      // Report before the message is flattened. The native sheet fails for
      // reasons the user can do nothing about and that no auth/* code covers —
      // a signing certificate missing from the Firebase project throws
      // ApiException 10 and shows no account picker at all — and every one of
      // them lands on the generic "something went wrong" toast. Without this
      // the only trace is adb logcat, which needs the phone on a cable.
      logger.error("Google sign-in failed", error, { native: Capacitor.isNativePlatform() })
      throw new Error(authErrorMessage(error))
    }
  }

  async signInWithApple(): Promise<AuthUser> {
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithApple()
        const idToken = result.credential?.idToken
        if (!idToken) throw new Error("No ID token received from Apple Sign-In")
        const credential = new OAuthProvider("apple.com").credential({
          idToken,
          rawNonce: result.credential?.nonce,
        })
        return formatUser((await signInWithCredential(auth, credential)).user)
      }
      return formatUser((await signInWithPopup(auth, appleProvider)).user)
    } catch (error) {
      logger.error("Apple sign-in failed", error, { native: Capacitor.isNativePlatform() })
      throw new Error(authErrorMessage(error))
    }
  }

  async signInAnonymously(): Promise<AuthUser> {
    try {
      return formatUser((await firebaseSignInAnonymously(auth)).user)
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  // Upgrading an anonymous user: linking keeps the uid, so all data rows
  // created as a guest stay owned by the account.
  async linkAnonymousWithEmail({ email, password, displayName }: RegisterData): Promise<AuthUser> {
    const user = auth.currentUser
    if (!user) throw new Error("Not signed in")
    try {
      const credential = await linkWithCredential(user, EmailAuthProvider.credential(email, password))
      if (displayName) {
        await firebaseUpdateProfile(credential.user, { displayName })
      }
      return formatUser(credential.user)
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  async linkAnonymousWithGoogle(): Promise<AuthUser> {
    const user = auth.currentUser
    if (!user) throw new Error("Not signed in")
    try {
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle()
        const idToken = result.credential?.idToken
        if (!idToken) throw new Error("No ID token received from Google Sign-In")
        return formatUser((await linkWithCredential(user, GoogleAuthProvider.credential(idToken))).user)
      }
      return formatUser((await linkWithPopup(user, googleProvider)).user)
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      // No custom continue URL: the reset link opens Firebase's hosted
      // set-new-password page, which works identically on web and native
      // (the in-app oobCode path below stays as a fallback should the
      // console's action URL ever point back at the app).
      await sendPasswordResetEmail(auth, email)
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  // Subscribe to "the user arrived via a password-reset link". Fires
  // immediately if recovery is already pending, so late subscribers (the
  // modal mounts after init) don't miss it.
  onPasswordRecovery(callback: () => void): () => void {
    this.passwordRecoveryListeners.add(callback)
    if (this.recoveryOobCode) callback()
    return () => this.passwordRecoveryListeners.delete(callback)
  }

  clearPasswordRecovery(): void {
    this.recoveryOobCode = null
  }

  async updatePassword(password: string): Promise<void> {
    try {
      if (this.recoveryOobCode) {
        await confirmPasswordReset(auth, this.recoveryOobCode, password)
      } else {
        const user = auth.currentUser
        if (!user) throw new Error("Not signed in")
        await firebaseUpdatePassword(user, password)
      }
      this.clearPasswordRecovery()
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  async logout(): Promise<void> {
    try {
      // Also clears the plugin's native session so the next provider sign-in
      // re-prompts for an account instead of silently reusing the last one.
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut().catch((error) => {
          logger.warn("Native sign-out failed", error)
        })
      }
      await signOut(auth)
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  getCurrentUser(): AuthUser | null {
    return this.cachedUser
  }

  // The bearer token apiClient/aiChatService attach to API requests. The SDK
  // refreshes it automatically (forceRefresh not needed); null when signed out.
  async getIdToken(): Promise<string | null> {
    const user = auth.currentUser
    if (!user) return null
    try {
      return await user.getIdToken()
    } catch (error) {
      logger.error("Failed to get ID token", error)
      return null
    }
  }

  // `onError` maps to the observer's error channel. Firebase reports a failed
  // persistence read / token refresh there and then never calls the next
  // handler, so a caller that only waits on `callback` waits forever — see
  // initializeAuth, which gates the whole app on this resolving.
  onAuthStateChanged(callback: (user: AuthUser | null) => void, onError?: (error: unknown) => void): () => void {
    return firebaseOnAuthStateChanged(
      auth,
      (user) => {
        callback(user ? formatUser(user) : null)
      },
      (error) => {
        logger.error("Auth state listener failed", error)
        onError?.(error)
      },
    )
  }

  async updateDisplayName(displayName: string): Promise<void> {
    const user = auth.currentUser
    if (!user) throw new Error("Not signed in")
    try {
      await firebaseUpdateProfile(user, { displayName })
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }

  async updateProfile({ displayName, photoURL }: { displayName?: string; photoURL?: string | null }): Promise<void> {
    const user = auth.currentUser
    if (!user) throw new Error("Not signed in")
    const update: { displayName?: string; photoURL?: string | null } = {}
    if (displayName !== undefined) update.displayName = displayName
    if (photoURL !== undefined) update.photoURL = photoURL || null
    if (Object.keys(update).length === 0) return
    try {
      await firebaseUpdateProfile(user, update)
    } catch (error) {
      throw new Error(authErrorMessage(error))
    }
  }
}

export const authService = new AuthService()
export default authService
